import type { JSX as PreactJSX } from "preact";
import { signal, computed } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { Button } from "@app/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@app/ui/components/card";
import { Input } from "@app/ui/components/input";
import { Label } from "@app/ui/components/label";
import { Switch } from "@app/ui/components/switch";
import { Alert, AlertDescription, AlertTitle } from "@app/ui/components/alert";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  Shield,
  Building2,
  GraduationCap,
  UserRound,
  LogOut,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  ChevronRight,
  Headphones,
  Sun,
  Moon,
  Eye,
  EyeOff,
  Fingerprint,
  Loader2
} from "lucide-react";
import {
  startAuthentication,
  browserSupportsWebAuthn
} from "@simplewebauthn/browser";
import {
  api,
  ApiError,
  type AvailableContext,
  type UserMin,
  type UserProfile
} from "./api";
import { getActiveTheme, toggleTheme } from "@app/ui/lib/theme";
import { brandingSignal, loadBranding } from "@app/ui/lib/branding";

// =============================================================================
// Estado (signals)
// =============================================================================

type Phase =
  | "loading"
  | "login"
  | "signup"
  | "forgot"
  | "change-password"
  | "picker"
  | "redirect";

const phase = signal<Phase>("loading");
const user = signal<UserMin | null>(null);
const contexts = signal<AvailableContext[]>([]);
const profiles = signal<UserProfile[]>([]);
const activeContext = signal<AvailableContext | null>(null);
const errorMsg = signal<string | null>(null);
const submitting = signal<boolean>(false);

// "Lembrar email neste dispositivo" — persistido em localStorage. Hidrata o
// form no boot pra evitar o usuário digitar email toda vez. Defaultmente ON
// se o user marcou na última visita.
const REMEMBER_KEY = "auth-remember-email";
function readRememberedEmail(): string {
  try {
    return localStorage.getItem(REMEMBER_KEY) || "";
  } catch {
    return "";
  }
}
function writeRememberedEmail(email: string | null) {
  try {
    if (email) localStorage.setItem(REMEMBER_KEY, email);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch {
    // ignore
  }
}

const formEmail = signal<string>(readRememberedEmail());
const formPassword = signal<string>("");
const remember = signal<boolean>(!!readRememberedEmail());

const themeSignal = signal<"light" | "dark">(getActiveTheme());
const showPassword = signal<boolean>(false);
if (typeof window !== "undefined") {
  window.addEventListener("theme:changed", (e: any) => {
    themeSignal.value = e?.detail?.theme ?? getActiveTheme();
  });
}

// =============================================================================
// Bootstrap
// =============================================================================

function wantsPicker(): boolean {
  // `?picker=1` força mostrar o picker mesmo se o user já tem activeContext.
  // Usado pelo botão "Trocar perfil" nos paineis pra evitar bounce loop.
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("picker") === "1";
}

// Detecta se a URL aponta pra signup/forgot — usado pelo bootstrap pra
// abrir direto na phase certa quando o usuário cola um link.
function wantsSignup(): boolean {
  if (typeof window === "undefined") return false;
  return /\/signup\/?$/.test(window.location.pathname);
}
function wantsForgot(): boolean {
  if (typeof window === "undefined") return false;
  return /\/forgot\/?$/.test(window.location.pathname);
}

/** Hidrata o email do signup a partir da URL e remove qualquer dado sensível
 *  da query string imediatamente, ANTES de qualquer outra coisa.
 *  [[project-signup-invite-token-security]]
 *
 *  Caminhos suportados (em ordem de preferência):
 *  1. `?t=<token>` → POST /api/auth/signup-invite-resolve, pega email do server.
 *  2. `?email=<x>` (LEGADO — convites enviados antes do fix). Preenchido direto.
 *
 *  Em ambos os casos, history.replaceState limpa a URL pra `/auth/signup`
 *  sem query string, evitando vazamento em history/Referer. */
async function hydrateSignupFromUrl() {
  if (typeof window === "undefined") return;
  const u = new URL(window.location.href);
  const token = u.searchParams.get("t");
  const legacyEmail = u.searchParams.get("email");
  // Código de indicação (referral) — opaco, seguro pra ficar na URL e na
  // history. NÃO limpamos da barra junto do email/token porque o aluno pode
  // querer copiar o link adiante. Mas validamos imediatamente e só pre-fill
  // se for válido. [[project-referral-system]]
  const refRaw = u.searchParams.get("ref");
  const ref = refRaw ? refRaw.trim().toUpperCase() : "";
  if (ref && /^[A-Z0-9]{6}$/.test(ref)) {
    signupReferralCode.value = ref;
    // Valida async — UI vai mostrar "Indicação de Luís M." quando voltar.
    void validateReferralCodeInline();
  }
  // Limpa a URL ANTES de tudo — assim mesmo que o fetch demore, a barra
  // de endereço já está sem o email.
  if (token || legacyEmail) {
    try {
      history.replaceState(null, "", u.pathname);
    } catch {
      // Ambientes muito antigos podem barrar replaceState — segue o jogo.
    }
  }
  if (token) {
    try {
      const r = await fetch("/api/auth/signup-invite-resolve", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token })
      });
      if (r.ok) {
        const data = (await r.json()) as { email?: string };
        if (typeof data.email === "string") {
          signupEmail.value = data.email;
        }
      }
      // Token inválido: deixa o form vazio, aluno digita manualmente.
    } catch {
      /* fail silent — fluxo manual ainda funciona */
    }
    return;
  }
  if (legacyEmail) {
    signupEmail.value = legacyEmail;
  }
}

async function bootstrap() {
  if (wantsSignup()) {
    await hydrateSignupFromUrl();
    phase.value = "signup";
    return;
  }
  if (wantsForgot()) {
    phase.value = "forgot";
    return;
  }
  try {
    const me = await api.me();
    user.value = me.user;
    contexts.value = me.contexts;
    profiles.value = me.profiles || [];
    activeContext.value = me.activeContext;
    if (me.activeContext && !wantsPicker()) {
      redirectToDestination();
      return;
    }
    // Auto-redirect quando user tem exatamente 1 perfil. Mesmo via ?picker=1
    // só faz sentido escolher se há 2+. Se for 1, mostra o picker mas vamos
    // dar um feedback elegante (apenas 1 perfil disponível).
    if (profiles.value.length === 1 && !wantsPicker()) {
      await autoSelectProfile(profiles.value[0]);
      return;
    }
    phase.value =
      profiles.value.length > 0 ? "picker" : "login";
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      phase.value = "login";
    } else {
      errorMsg.value = err instanceof Error ? err.message : String(err);
      phase.value = "login";
    }
  }
}

async function autoSelectProfile(p: UserProfile) {
  try {
    const res = await api.switchProfile({
      kind: p.kind,
      role: p.role,
      companyId: p.kind === "company" ? p.companies[0]?.id : undefined
    });
    activeContext.value = res.activeContext;
    redirectToDestination();
  } catch (err) {
    errorMsg.value = err instanceof Error ? err.message : "Erro ao entrar";
    phase.value = "picker";
  }
}

async function handleLogin(e: Event) {
  e.preventDefault();
  errorMsg.value = null;
  submitting.value = true;
  try {
    const res = await api.login(formEmail.value, formPassword.value);
    user.value = res.user;
    contexts.value = res.contexts;
    profiles.value = res.profiles || [];
    activeContext.value = null;
    // Guarda a senha atual pra usar como currentPassword no change-password.
    // Limpa só depois da troca (ou na navegação pra picker).
    if (res.user.mustChangePassword) {
      currentPasswordForChange.value = formPassword.value;
      formPassword.value = "";
      writeRememberedEmail(remember.value ? formEmail.value : null);
      phase.value = "change-password";
      return;
    }
    formPassword.value = "";
    writeRememberedEmail(remember.value ? formEmail.value : null);
    if (profiles.value.length === 1) {
      await autoSelectProfile(profiles.value[0]);
      return;
    }
    phase.value = profiles.value.length === 0 ? "redirect" : "picker";
  } catch (err) {
    // Conta bloqueada após 3 tentativas erradas — exibe mensagem específica
    // com call-to-action explícito para "Esqueci minha senha".
    // [[project-account-lockout]]
    if (err instanceof ApiError && err.code === "password_locked") {
      errorMsg.value =
        "Sua conta foi bloqueada após 3 tentativas incorretas. Clique em \"Esqueci minha senha\" abaixo para criar uma nova.";
    } else {
      errorMsg.value = err instanceof Error ? err.message : "Erro no login";
    }
  } finally {
    submitting.value = false;
  }
}

async function handlePasskeyLogin() {
  errorMsg.value = null;
  const email = formEmail.value.trim();
  if (!email) {
    errorMsg.value = "Digite o e-mail antes de usar a biometria.";
    return;
  }
  submitting.value = true;
  try {
    const opts = await api.passkeyLoginOptions(email);
    const credential = await startAuthentication({ optionsJSON: opts.options });
    const res = await api.passkeyLoginVerify({
      email,
      challengeId: opts.challengeId,
      credential
    });
    user.value = res.user;
    contexts.value = res.contexts;
    profiles.value = res.profiles || [];
    activeContext.value = null;
    formPassword.value = "";
    writeRememberedEmail(remember.value ? formEmail.value : null);
    if (profiles.value.length === 1) {
      await autoSelectProfile(profiles.value[0]);
      return;
    }
    phase.value = profiles.value.length === 0 ? "redirect" : "picker";
  } catch (err: any) {
    const name = err?.name || "";
    const msg = err?.message || String(err);
    console.warn("[passkey-login] err:", name, msg);
    if (err instanceof ApiError && err.code === "no_passkeys") {
      errorMsg.value = "Nenhuma biometria cadastrada para este e-mail.";
    } else if (/timed?\s?out|timeout/i.test(msg)) {
      errorMsg.value =
        "Tempo esgotado aguardando a biometria. Tente de novo.";
    } else if (
      name === "NotAllowedError" ||
      /not\s?allowed|cancell?ed|aborted/i.test(msg)
    ) {
      // NotAllowedError cobre tanto cancelamento explícito quanto timeout do
      // próprio dispositivo. Mensagem genérica que serve pros dois.
      errorMsg.value =
        "Não foi possível usar a biometria agora. Verifique se o sensor está disponível e tente novamente.";
    } else if (name === "InvalidStateError") {
      errorMsg.value =
        "Esta biometria não está cadastrada para este e-mail.";
    } else if (name === "SecurityError") {
      errorMsg.value =
        "O navegador bloqueou a biometria. Acesse o site pelo HTTPS e tente de novo.";
    } else if (
      typeof navigator !== "undefined" &&
      !navigator.onLine
    ) {
      errorMsg.value =
        "Sem conexão com a internet. Verifique sua rede e tente de novo.";
    } else {
      errorMsg.value =
        "Não foi possível entrar com biometria agora. Tente de novo ou use sua senha.";
    }
  } finally {
    submitting.value = false;
  }
}

async function handlePick(ctx: AvailableContext) {
  errorMsg.value = null;
  submitting.value = true;
  try {
    const res = await api.switchContext(ctx);
    activeContext.value = res.activeContext;
    redirectToDestination();
  } catch (err) {
    errorMsg.value = err instanceof Error ? err.message : "Erro ao trocar de contexto";
    submitting.value = false;
  }
}

async function handlePickProfile(p: UserProfile) {
  errorMsg.value = null;
  submitting.value = true;
  try {
    const res = await api.switchProfile({
      kind: p.kind,
      role: p.role,
      // Pra company-roles, pega a primeira company (prof/admin_company depois
      // alternam dentro do painel via switcher; student vê tudo unificado).
      companyId: p.kind === "company" ? p.companies[0]?.id : undefined
    });
    activeContext.value = res.activeContext;
    redirectToDestination();
  } catch (err) {
    errorMsg.value = err instanceof Error ? err.message : "Erro ao escolher perfil";
    submitting.value = false;
  }
}

function redirectToDestination() {
  const dest = destinationFor.value;
  if (dest) {
    window.location.href = dest;
    return;
  }
  // Sem destino mapeado pra esse contexto — cai no fallback (mostra tela).
  phase.value = "redirect";
}

async function handleLogout() {
  await api.logout().catch(() => {});
  user.value = null;
  contexts.value = [];
  activeContext.value = null;
  errorMsg.value = null;
  formEmail.value = "";
  phase.value = "login";
}

// =============================================================================
// Mapping
// =============================================================================

const destinationFor = computed(() => {
  const ctx = activeContext.value;
  if (!ctx) return null;
  if (ctx.kind === "platform") return "/manager/";
  if (ctx.role === "admin_company") return "/company/";
  if (ctx.role === "teacher") return "/teacher/";
  if (ctx.role === "student") return "/student/";
  return null;
});

const roleMeta: Record<string, { icon: typeof Shield; label: string; tagline: string }> = {
  admin: { icon: Shield, label: "Admin da Plataforma", tagline: "Acesso global" },
  support: { icon: Headphones, label: "Suporte", tagline: "Atendimento" },
  admin_company: { icon: Building2, label: "Organização", tagline: "Gestão da escola" },
  teacher: { icon: GraduationCap, label: "Professor", tagline: "Disciplinas e turmas" },
  student: { icon: UserRound, label: "Aluno", tagline: "Estudos e provas" }
};

// Ordem dos perfis no picker: Aluno primeiro, Admin por último.
// [[feedback-profile-picker-order]] pedido 2026-05-19.
const ROLE_ORDER: Record<string, number> = {
  student: 0,
  teacher: 1,
  admin_company: 2,
  support: 3,
  admin: 4
};

function sortedProfiles(list: UserProfile[]): UserProfile[] {
  return [...list].sort((a, b) => {
    const oa = ROLE_ORDER[a.role] ?? 99;
    const ob = ROLE_ORDER[b.role] ?? 99;
    return oa - ob;
  });
}

// =============================================================================
// Decorações (divs nativos podem usar `class`)
// =============================================================================

function MeshBackground() {
  // Dark neutralizado — sem mesh violeta no fundo
  return null;
}

function GlowFrame({
  children,
  className = ""
}: {
  children: any;
  className?: string;
}) {
  // Sem glow violeta — apenas wrapper de largura
  return <div class={`relative w-full ${className}`}>{children}</div>;
}

// Botão de tema inline — usado dentro de cards (picker, redirect, login).
// Outline em vez de ghost: com texto ao lado do ícone, o usuário precisa
// enxergar a área clicável.
function ThemeToggleButton() {
  const isDark = themeSignal.value === "dark";
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => toggleTheme()}
      title={isDark ? "Tema claro (Ctrl+/)" : "Tema escuro (Ctrl+/)"}
      aria-label={isDark ? "Mudar para tema claro" : "Mudar para tema escuro"}
      className="shrink-0"
    >
      {isDark ? <Sun className="size-4 mr-1.5" /> : <Moon className="size-4 mr-1.5" />}
      {isDark ? "Claro" : "Escuro"}
    </Button>
  );
}

function BrandLogo() {
  const frames = [
    { icon: Sparkles },
    { icon: Shield },
    { icon: Building2 },
    { icon: GraduationCap },
    { icon: UserRound }
  ] as const;
  // Estado de animação em 3 fases para garantir que apenas UM ícone fique
  // visível por vez (sem sobreposição no centro):
  //   holding → "out" (outgoing slideOutLeft, incoming não existe) →
  //   "in" (incoming slideInRight, outgoing já saiu) → holding.
  const HOLD_MS = 980;
  const OUT_MS = 280;
  const IN_MS = 320;
  const [animationState, setAnimationState] = useState<{
    currentIndex: number;
    nextIndex: number;
    phase: "holding" | "out" | "in";
  }>({
    currentIndex: 0,
    nextIndex: 1 % 5,
    phase: "holding"
  });

  useEffect(() => {
    if (animationState.phase === "holding") {
      const timer = window.setTimeout(() => {
        setAnimationState((current) => ({
          currentIndex: current.currentIndex,
          nextIndex: (current.currentIndex + 1) % frames.length,
          phase: "out"
        }));
      }, HOLD_MS);
      return () => window.clearTimeout(timer);
    }
    if (animationState.phase === "out") {
      const timer = window.setTimeout(() => {
        setAnimationState((current) => ({
          currentIndex: current.nextIndex,
          nextIndex: (current.nextIndex + 1) % frames.length,
          phase: "in"
        }));
      }, OUT_MS);
      return () => window.clearTimeout(timer);
    }
    // phase === "in"
    const timer = window.setTimeout(() => {
      setAnimationState((current) => ({
        ...current,
        phase: "holding"
      }));
    }, IN_MS);
    return () => window.clearTimeout(timer);
  }, [animationState.phase]);

  const activeFrame = frames[animationState.currentIndex];

  return (
    <div class="flex flex-col items-center">
      <div class="relative grid size-16 place-items-center overflow-hidden rounded-2xl bg-brand-gradient text-white shadow-sm">
        <style>{`
          @keyframes brand-logo-slide-in {
            from {
              opacity: 0;
              transform: translate3d(110%, 0, 0) scale(0.92);
            }
            70% {
              opacity: 1;
              transform: translate3d(-3%, 0, 0) scale(1.02);
            }
            to {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
            }
          }
          @keyframes brand-logo-slide-out {
            from {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
            }
            to {
              opacity: 0;
              transform: translate3d(-110%, 0, 0) scale(0.92);
            }
          }
        `}</style>
        {(() => {
          const Icon = activeFrame.icon;
          const phase = animationState.phase;
          // Em "holding" e "in", currentIndex JÁ é o ícone novo. Em "out",
          // currentIndex ainda é o antigo (saindo).
          let style: PreactJSX.CSSProperties = {
            opacity: 1,
            transform: "translate3d(0, 0, 0) scale(1)"
          };
          if (phase === "out") {
            style = {
              animation: `brand-logo-slide-out ${OUT_MS}ms cubic-bezier(0.4, 0, 1, 1) both`
            };
          } else if (phase === "in") {
            style = {
              animation: `brand-logo-slide-in ${IN_MS}ms cubic-bezier(0.16, 1, 0.3, 1) both`
            };
          }
          return (
            <span
              key={`logo-${animationState.currentIndex}-${phase}`}
              class="absolute inset-0 grid place-items-center"
              style={style}
            >
              <Icon
                className="size-8"
                strokeWidth={2.15}
                style={{ color: "#ffffff", stroke: "#ffffff" }}
              />
            </span>
          );
        })()}
      </div>
    </div>
  );
}

function BrandHeader() {
  // Branding signal reativo — quando o admin troca em /manager/Identidade,
  // o public-branding atualiza e o signal propaga sem reload.
  const b = brandingSignal.value;
  return (
    <div class="mb-8 flex flex-col items-center text-center">
      <BrandLogo />
      <h1 class="mt-4 text-4xl font-extrabold tracking-tight text-foreground">
        {b.platformName}
      </h1>
      {b.loginSubtitle && (
        <p class="mt-2 text-sm text-muted-foreground">{b.loginSubtitle}</p>
      )}
    </div>
  );
}

/** Rodapé configurável pelo admin (BrandingView). Renderiza somente quando
 *  o texto está preenchido — sem texto, sem rodapé. */
function BrandFooter() {
  const b = brandingSignal.value;
  if (!b.footerText) return null;
  return (
    <p class="mt-6 max-w-md text-center text-[11px] text-muted-foreground/60">
      {b.footerText}
    </p>
  );
}

// ATENÇÃO: shadcn components usam `className` (React convention). Em Preact,
// se você passa `class` a uma function component, ela recebe literalmente
// `class` em props — e shadcn destrutura `className`, ignorando o seu valor
// (e SUBSTITUINDO os defaults via spread). Resultado: botão sem padding,
// sem display flex, sem rounded — só texto. SEMPRE use className aqui.
// Borda violeta visível + bg do card mais opaco que o default pra destacar
// claramente o card sobre o background dark (diferença pequena entre
// --background e --card no dark mode tornava o card invisível).
const CARD_CLASS =
  "rounded-md border-border bg-card shadow-sm";

function ShineButton({
  children,
  className = "",
  ...rest
}: any) {
  return (
    <Button
      {...rest}
      className={`border-0 bg-brand-gradient text-white shadow-sm transition-all hover:brightness-105 ${className}`}
    >
      {children}
    </Button>
  );
}

function FilledBrandBadge({
  icon: Icon,
  size = "md"
}: {
  icon: typeof Shield;
  size?: "md" | "lg";
}) {
  const shell = size === "lg" ? "size-16 rounded-2xl" : "size-12 rounded-xl";
  const iconSize = size === "lg" ? "size-8" : "size-6";
  return (
    <div class={`grid shrink-0 place-items-center bg-brand-gradient text-white shadow-sm ring-1 ring-white/10 ${shell}`}>
      <Icon className={iconSize} strokeWidth={2.25} />
    </div>
  );
}

// =============================================================================
// Pages
// =============================================================================

// =============================================================================
// Signup
// =============================================================================

type SignupStep = "email" | "otp" | "details" | "done" | "interest" | "interest-done";

const signupStep = signal<SignupStep>("email");
const signupEmail = signal<string>("");
const signupName = signal<string>("");
const signupSocialName = signal<string>("");
const signupPassword = signal<string>("");
const signupPasswordConfirm = signal<string>("");
const signupApelido = signal<string>("");
const signupPhone = signal<string>("");
const signupShowPassword = signal<boolean>(false);
const signupErrorMsg = signal<string | null>(null);
const signupSubmitting = signal<boolean>(false);

// === OTP de verificação de email ===
// Token JWT-like emitido por /signup-otp-verify após acertar o código.
// signupComplete exige ele — sem isso o backend rejeita 403.
const signupOtpCode = signal<string>("");
const signupOtpToken = signal<string>("");
const signupOtpResendCooldownSec = signal<number>(0);
const signupOtpInfo = signal<string | null>(null);

// === Indicação opcional ===
// Aceita pre-fill do `?ref=` na URL (preenchido em hydrateSignupFromUrl).
const signupReferralCode = signal<string>("");
const signupReferralIndicatorName = signal<string | null>(null);
const signupReferralChecking = signal<boolean>(false);
const signupReferralInvalid = signal<boolean>(false);

// Manifestação de interesse (quando email não está na allowlist)
const interestFullName = signal<string>("");
const interestStudyLocation = signal<string>("");
const interestStudyArea = signal<string>("");
const interestMessage = signal<string>("");

// CRÍTICO — vazamento de dados entre usuários no mesmo browser. Toda vez que
// entramos no fluxo de cadastro/manifestação a memória precisa estar 100%
// limpa. Sem isso, pessoa A preenche, pessoa B troca o email e vê o nome de
// A na tela. Incidente reportado 2026-05-19.
//
// Política: chamar resetSignupForm() em qualquer entrada/saída do fluxo, e
// resetDownstreamFields() sempre que o email muda (ou o usuário clica em
// "Trocar email") pra zerar todo dado pessoal coletado depois do email.
function resetSignupForm() {
  signupEmail.value = "";
  signupName.value = "";
  signupSocialName.value = "";
  signupPassword.value = "";
  signupPasswordConfirm.value = "";
  signupApelido.value = "";
  signupPhone.value = "";
  signupShowPassword.value = false;
  signupErrorMsg.value = null;
  signupOtpCode.value = "";
  signupOtpToken.value = "";
  signupOtpInfo.value = null;
  signupReferralCode.value = "";
  signupReferralIndicatorName.value = null;
  signupReferralInvalid.value = false;
  interestFullName.value = "";
  interestStudyLocation.value = "";
  interestStudyArea.value = "";
  interestMessage.value = "";
}

function resetDownstreamFields() {
  signupName.value = "";
  signupSocialName.value = "";
  signupPassword.value = "";
  signupPasswordConfirm.value = "";
  signupApelido.value = "";
  signupPhone.value = "";
  signupShowPassword.value = false;
  signupOtpCode.value = "";
  signupOtpToken.value = "";
  signupOtpInfo.value = null;
  // referralCode NÃO entra aqui — vem da URL e deve persistir mesmo se o
  // usuário troca o email digitado.
  interestFullName.value = "";
  interestStudyLocation.value = "";
  interestStudyArea.value = "";
  interestMessage.value = "";
}

function goToSignup() {
  resetSignupForm();
  signupStep.value = "email";
  phase.value = "signup";
  if (typeof window !== "undefined") {
    history.pushState(null, "", "/auth/signup");
  }
}

function backToLogin() {
  resetSignupForm();
  signupStep.value = "email";
  phase.value = "login";
  if (typeof window !== "undefined") {
    history.pushState(null, "", "/auth/");
  }
}

function validatePasswordClient(pw: string): string | null {
  if (pw.length < 8) return "A senha deve ter pelo menos 8 caracteres.";
  if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw))
    return "A senha precisa ter letras e números.";
  return null;
}

async function handleSignupCheckEmail(e: Event) {
  e.preventDefault();
  signupErrorMsg.value = null;
  const email = signupEmail.value.trim();
  if (!email || !email.includes("@")) {
    signupErrorMsg.value = "Digite um email válido.";
    return;
  }
  signupSubmitting.value = true;
  try {
    const r = await api.signupCheck(email);
    if (!r.canProceed) {
      // Não autorizado: oferece formulário de manifestação de interesse em
      // vez de barrar com mensagem genérica. Anti-enumeration mantido pelo
      // próprio endpoint signupInterest (responde 200 mesmo se já existe).
      signupStep.value = "interest";
      return;
    }
    // Email autorizado → dispara OTP e vai pro step de código.
    await api.signupOtpSend(email);
    signupOtpInfo.value =
      "Enviamos um código de 6 dígitos pro seu email. Se não chegar em 1 minuto, confira a caixa de spam ou peça outro.";
    startOtpCooldown(60);
    signupStep.value = "otp";
  } catch (err) {
    signupErrorMsg.value = err instanceof Error ? err.message : String(err);
  } finally {
    signupSubmitting.value = false;
  }
}

let otpCooldownTimer: ReturnType<typeof setInterval> | null = null;
function startOtpCooldown(secs: number) {
  signupOtpResendCooldownSec.value = secs;
  if (otpCooldownTimer) clearInterval(otpCooldownTimer);
  otpCooldownTimer = setInterval(() => {
    const left = signupOtpResendCooldownSec.value - 1;
    if (left <= 0) {
      signupOtpResendCooldownSec.value = 0;
      if (otpCooldownTimer) clearInterval(otpCooldownTimer);
      otpCooldownTimer = null;
    } else {
      signupOtpResendCooldownSec.value = left;
    }
  }, 1000);
}

async function handleSignupOtpVerify(e: Event) {
  e.preventDefault();
  signupErrorMsg.value = null;
  const code = signupOtpCode.value.trim();
  if (!/^\d{6}$/.test(code)) {
    signupErrorMsg.value = "Digite os 6 dígitos do código.";
    return;
  }
  signupSubmitting.value = true;
  try {
    const r = await api.signupOtpVerify(signupEmail.value.trim(), code);
    signupOtpToken.value = r.otpToken;
    signupErrorMsg.value = null;
    signupStep.value = "details";
  } catch (err) {
    signupErrorMsg.value = err instanceof Error ? err.message : String(err);
  } finally {
    signupSubmitting.value = false;
  }
}

async function handleSignupOtpResend() {
  if (signupOtpResendCooldownSec.value > 0) return;
  signupErrorMsg.value = null;
  signupSubmitting.value = true;
  try {
    await api.signupOtpSend(signupEmail.value.trim());
    signupOtpInfo.value = "Novo código enviado.";
    startOtpCooldown(60);
  } catch (err) {
    signupErrorMsg.value = err instanceof Error ? err.message : String(err);
  } finally {
    signupSubmitting.value = false;
  }
}

async function validateReferralCodeInline() {
  const code = signupReferralCode.value.trim().toUpperCase();
  if (code.length !== 6) {
    signupReferralIndicatorName.value = null;
    signupReferralInvalid.value = false;
    return;
  }
  signupReferralChecking.value = true;
  signupReferralInvalid.value = false;
  try {
    const r = await api.referralValidate(code);
    if (r.valid && r.indicatorDisplayName) {
      signupReferralIndicatorName.value = r.indicatorDisplayName;
      signupReferralInvalid.value = false;
    } else {
      signupReferralIndicatorName.value = null;
      signupReferralInvalid.value = true;
    }
  } catch {
    signupReferralIndicatorName.value = null;
    signupReferralInvalid.value = true;
  } finally {
    signupReferralChecking.value = false;
  }
}

async function handleSignupInterest(e: Event) {
  e.preventDefault();
  signupErrorMsg.value = null;
  const fullName = interestFullName.value.trim();
  if (!fullName || fullName.length < 3) {
    signupErrorMsg.value = "Informe seu nome completo.";
    return;
  }
  signupSubmitting.value = true;
  try {
    await api.signupInterest({
      email: signupEmail.value.trim(),
      fullName,
      studyLocation: interestStudyLocation.value.trim() || undefined,
      studyArea: interestStudyArea.value.trim() || undefined,
      interestMessage: interestMessage.value.trim() || undefined
    });
    signupStep.value = "interest-done";
  } catch (err) {
    signupErrorMsg.value = err instanceof Error ? err.message : String(err);
  } finally {
    signupSubmitting.value = false;
  }
}

async function handleSignupComplete(e: Event) {
  e.preventDefault();
  signupErrorMsg.value = null;
  const name = signupName.value.trim();
  if (!name) {
    signupErrorMsg.value = "Nome completo é obrigatório.";
    return;
  }
  if (signupPassword.value !== signupPasswordConfirm.value) {
    signupErrorMsg.value = "As senhas não conferem.";
    return;
  }
  const pwErr = validatePasswordClient(signupPassword.value);
  if (pwErr) {
    signupErrorMsg.value = pwErr;
    return;
  }
  signupSubmitting.value = true;
  try {
    const res = await api.signupComplete({
      email: signupEmail.value.trim(),
      password: signupPassword.value,
      name,
      socialName: signupSocialName.value.trim() || undefined,
      apelido: signupApelido.value.trim() || undefined,
      phone: signupPhone.value.trim() || undefined,
      otpToken: signupOtpToken.value,
      // Envia o código SEMPRE que for bem-formado (6 alfanuméricos), sem
      // depender da validação assíncrona ter populado o nome do indicador — o
      // backend revalida best-effort e só atribui se o código existir/ativo.
      // Bug 2026-05-29: o gate em signupReferralIndicatorName fazia o código
      // quase nunca ser enviado → 0 de 37 cadastros ficaram atribuídos.
      referralCode: (() => {
        const c = signupReferralCode.value.trim().toUpperCase();
        return /^[A-Z0-9]{6}$/.test(c) ? c : undefined;
      })()
    });
    user.value = res.user;
    contexts.value = res.contexts;
    profiles.value = res.profiles || [];
    activeContext.value = null;
    if (profiles.value.length === 1) {
      await autoSelectProfile(profiles.value[0]);
      return;
    }
    phase.value = profiles.value.length === 0 ? "redirect" : "picker";
  } catch (err) {
    signupErrorMsg.value = err instanceof Error ? err.message : "Erro ao cadastrar.";
  } finally {
    signupSubmitting.value = false;
  }
}

// =============================================================================
// Forgot password
// =============================================================================

const forgotEmail = signal<string>("");
const forgotSubmitting = signal<boolean>(false);
const forgotDone = signal<boolean>(false);

// Senha "atual" guardada da tela de login pra mandar como currentPassword
// no /change-password sem o user precisar digitar de novo. Limpa após troca.
const currentPasswordForChange = signal<string>("");

function goToForgot() {
  errorMsg.value = null;
  forgotEmail.value = formEmail.value || "";
  forgotDone.value = false;
  phase.value = "forgot";
  if (typeof window !== "undefined") {
    history.pushState(null, "", "/auth/forgot");
  }
}

function backFromForgot() {
  forgotDone.value = false;
  forgotEmail.value = "";
  phase.value = "login";
  if (typeof window !== "undefined") {
    history.pushState(null, "", "/auth/");
  }
}

async function handleForgotSubmit(e: Event) {
  e.preventDefault();
  errorMsg.value = null;
  forgotSubmitting.value = true;
  try {
    await api.forgotPassword(forgotEmail.value.trim());
    // SEMPRE retorna ok — não dizemos se o email existe.
    forgotDone.value = true;
  } catch (err) {
    // Se chegou aqui, provavelmente é um problema técnico (rede etc).
    // Mostramos a mesma mensagem genérica.
    forgotDone.value = true;
    console.warn("forgot password err:", err);
  } finally {
    forgotSubmitting.value = false;
  }
}

function ForgotPasswordPage() {
  if (forgotDone.value) {
    return (
      <GlowFrame className="max-w-md">
        <Card className={CARD_CLASS}>
          <CardHeader className="items-center px-8 pb-4 pt-8 text-center">
            <CardTitle className="text-2xl">Verifique seu email</CardTitle>
            <CardDescription>
              Se houver uma conta com esse endereço, enviamos uma senha
              temporária. Verifique a caixa de entrada (e também o spam).
            </CardDescription>
          </CardHeader>
          <CardContent className="px-8 pb-8 pt-0">
            <p class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              <AlertCircle className="mr-1 inline size-3.5" />
              Por segurança, não informamos se o email está cadastrado. Caso
              não receba em alguns minutos, confira se digitou correto e tente
              de novo.
            </p>
            <button
              type="button"
              onClick={backFromForgot}
              class="mt-5 inline-flex w-full items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              Voltar ao login
            </button>
          </CardContent>
        </Card>
      </GlowFrame>
    );
  }

  return (
    <GlowFrame className="max-w-md">
      <Card className={CARD_CLASS}>
        <CardHeader className="items-center px-8 pb-4 pt-8 text-center">
          <CardTitle className="text-2xl">Esqueci minha senha</CardTitle>
          <CardDescription>
            Digite seu email. Se houver uma conta, mandamos uma senha
            temporária por email — você troca por uma nova no próximo login.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-8 pt-0">
          <form class="space-y-4" onSubmit={handleForgotSubmit}>
            <div class="space-y-2">
              <Label
                htmlFor="forgot-email"
                className="text-xs uppercase tracking-wider text-muted-foreground"
              >
                E-mail
              </Label>
              <Input
                id="forgot-email"
                type="email"
                required
                autoComplete="email"
                placeholder="voce@email.com"
                value={forgotEmail.value}
                onInput={(e: any) => (forgotEmail.value = e.target.value)}
                className="h-11"
              />
            </div>
            <ShineButton
              type="submit"
              disabled={forgotSubmitting.value}
              className="mt-2 h-11 w-full text-base font-medium"
            >
              {forgotSubmitting.value ? "Enviando…" : "Enviar senha temporária"}
            </ShineButton>
            <div class="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={backFromForgot}
                class="text-xs text-muted-foreground transition-colors hover:text-primary"
              >
                ← Voltar ao login
              </button>
              <ThemeToggleButton />
            </div>
          </form>
        </CardContent>
      </Card>
    </GlowFrame>
  );
}

// =============================================================================
// Change password forçado
// =============================================================================

const changeNewPw = signal<string>("");
const changeConfirmPw = signal<string>("");
const changeShowPw = signal<boolean>(false);
const changeSubmitting = signal<boolean>(false);
const changeErrorMsg = signal<string | null>(null);

function validateNewPassword(pw: string): string | null {
  if (pw.length < 8) return "A senha deve ter pelo menos 8 caracteres.";
  if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw))
    return "A senha precisa ter letras e números.";
  return null;
}

async function handleChangePassword(e: Event) {
  e.preventDefault();
  changeErrorMsg.value = null;
  const err = validateNewPassword(changeNewPw.value);
  if (err) {
    changeErrorMsg.value = err;
    return;
  }
  if (changeNewPw.value !== changeConfirmPw.value) {
    changeErrorMsg.value = "As senhas não conferem.";
    return;
  }
  changeSubmitting.value = true;
  try {
    await api.changePassword(currentPasswordForChange.value, changeNewPw.value);
    // Limpa estado sensível
    currentPasswordForChange.value = "";
    changeNewPw.value = "";
    changeConfirmPw.value = "";
    // Atualiza user no signal
    if (user.value) {
      user.value = { ...user.value, mustChangePassword: false };
    }
    // Segue o fluxo normal pós-login
    if (profiles.value.length === 1) {
      await autoSelectProfile(profiles.value[0]);
      return;
    }
    phase.value = profiles.value.length === 0 ? "redirect" : "picker";
  } catch (e) {
    changeErrorMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    changeSubmitting.value = false;
  }
}

function ChangePasswordPage() {
  return (
    <GlowFrame className="max-w-md">
      <Card className={CARD_CLASS}>
        <CardHeader className="items-center px-8 pb-4 pt-8 text-center">
          <CardTitle className="text-2xl">Crie uma nova senha</CardTitle>
          <CardDescription>
            Você entrou com uma senha temporária. Por segurança, escolha uma
            nova senha antes de continuar.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-8 pt-0">
          <form class="space-y-4" onSubmit={handleChangePassword}>
            <div class="space-y-2">
              <Label
                htmlFor="new-pw"
                className="text-xs uppercase tracking-wider text-muted-foreground"
              >
                Nova senha
              </Label>
              <div class="relative">
                <Input
                  id="new-pw"
                  type={changeShowPw.value ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  placeholder="Mínimo 8 caracteres, letras + números"
                  value={changeNewPw.value}
                  onInput={(e: any) => (changeNewPw.value = e.target.value)}
                  className="h-11 pr-11"
                />
                <button
                  type="button"
                  onClick={() => (changeShowPw.value = !changeShowPw.value)}
                  aria-label={changeShowPw.value ? "Ocultar senha" : "Mostrar senha"}
                  title={changeShowPw.value ? "Ocultar senha" : "Mostrar senha"}
                  class="absolute inset-y-0 right-0 grid w-11 place-items-center text-muted-foreground transition-colors hover:text-primary"
                >
                  {changeShowPw.value ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
            </div>
            <div class="space-y-2">
              <Label
                htmlFor="confirm-pw"
                className="text-xs uppercase tracking-wider text-muted-foreground"
              >
                Confirmar nova senha
              </Label>
              <Input
                id="confirm-pw"
                type={changeShowPw.value ? "text" : "password"}
                required
                autoComplete="new-password"
                value={changeConfirmPw.value}
                onInput={(e: any) => (changeConfirmPw.value = e.target.value)}
                className="h-11"
              />
            </div>
            {changeErrorMsg.value && (
              <Alert
                variant="destructive"
                className="border-destructive/40 bg-destructive/10"
              >
                <AlertCircle className="size-4" />
                <AlertTitle>Não foi possível trocar</AlertTitle>
                <AlertDescription>{changeErrorMsg.value}</AlertDescription>
              </Alert>
            )}
            <ShineButton
              type="submit"
              disabled={changeSubmitting.value}
              className="mt-2 h-11 w-full text-base font-medium"
            >
              {changeSubmitting.value ? "Salvando…" : "Salvar nova senha"}
            </ShineButton>
          </form>
        </CardContent>
      </Card>
    </GlowFrame>
  );
}

function SignupPage() {
  const step = signupStep.value;
  return (
    <GlowFrame className="max-w-md">
      <Card className={CARD_CLASS}>
        <CardHeader className="items-center px-8 pb-4 pt-8 text-center">
          <CardTitle className="text-2xl">Criar conta</CardTitle>
          <CardDescription>
            {step === "email"
              ? "Comece com seu email — vamos verificar se você foi convidado."
              : step === "otp"
                ? "Digite o código que enviamos pro seu email."
                : "Preencha seus dados pra concluir o cadastro."}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-8 pt-0">
          {step === "email" && (
            <form class="space-y-5" onSubmit={handleSignupCheckEmail}>
              <div class="space-y-2">
                <Label
                  htmlFor="signup-email"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  E-mail
                </Label>
                <Input
                  id="signup-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="voce@email.com"
                  value={signupEmail.value}
                  onInput={(e: any) => {
                    const prev = signupEmail.value;
                    const next = e.target.value;
                    if (next !== prev) {
                      // Email mudou — zera todo dado pessoal que possa ter
                      // ficado de uma tentativa anterior. Sem isso, pessoa
                      // que reabre /signup com email diferente vê o nome do
                      // ocupante anterior do device.
                      resetDownstreamFields();
                    }
                    signupEmail.value = next;
                  }}
                  className="h-11"
                />
              </div>
              {signupErrorMsg.value && (
                <Alert
                  variant="destructive"
                  className="border-destructive/40 bg-destructive/10"
                >
                  <AlertCircle className="size-4" />
                  <AlertTitle>Não foi possível continuar</AlertTitle>
                  <AlertDescription>{signupErrorMsg.value}</AlertDescription>
                </Alert>
              )}
              <ShineButton
                type="submit"
                disabled={signupSubmitting.value}
                className="mt-2 h-11 w-full text-base font-medium"
              >
                {signupSubmitting.value ? "Verificando…" : "Continuar"}
              </ShineButton>
              <div class="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={backToLogin}
                  class="text-xs text-muted-foreground transition-colors hover:text-primary"
                >
                  ← Voltar ao login
                </button>
                <ThemeToggleButton />
              </div>
            </form>
          )}

          {step === "otp" && (
            <form class="space-y-4" onSubmit={handleSignupOtpVerify}>
              <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs">
                Código enviado pra <span class="font-medium">{signupEmail.value}</span>
                <button
                  type="button"
                  onClick={() => {
                    resetDownstreamFields();
                    signupStep.value = "email";
                  }}
                  class="ml-2 text-emerald-600 underline hover:no-underline"
                >
                  trocar
                </button>
              </div>

              {signupOtpInfo.value && (
                <p class="text-xs text-muted-foreground">{signupOtpInfo.value}</p>
              )}

              <div class="space-y-2">
                <Label
                  htmlFor="signup-otp"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  Código de 6 dígitos
                </Label>
                <Input
                  id="signup-otp"
                  type="text"
                  inputmode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  placeholder="000000"
                  value={signupOtpCode.value}
                  onInput={(e: any) => {
                    const onlyDigits = String(e.target.value ?? "")
                      .replace(/\D/g, "")
                      .slice(0, 6);
                    signupOtpCode.value = onlyDigits;
                  }}
                  className="h-12 text-center text-2xl font-bold tracking-[0.5em]"
                  autoFocus
                />
              </div>

              {signupErrorMsg.value && (
                <Alert
                  variant="destructive"
                  className="border-destructive/40 bg-destructive/10"
                >
                  <AlertCircle className="size-4" />
                  <AlertTitle>Código incorreto</AlertTitle>
                  <AlertDescription>{signupErrorMsg.value}</AlertDescription>
                </Alert>
              )}

              <ShineButton
                type="submit"
                disabled={signupSubmitting.value || signupOtpCode.value.length !== 6}
                className="mt-2 h-11 w-full text-base font-medium"
              >
                {signupSubmitting.value ? "Verificando…" : "Verificar código"}
              </ShineButton>

              <div class="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={handleSignupOtpResend}
                  disabled={
                    signupSubmitting.value || signupOtpResendCooldownSec.value > 0
                  }
                  class="text-xs text-muted-foreground transition-colors hover:text-primary disabled:opacity-50"
                >
                  {signupOtpResendCooldownSec.value > 0
                    ? `Reenviar em ${signupOtpResendCooldownSec.value}s`
                    : "Não recebi — reenviar"}
                </button>
                <ThemeToggleButton />
              </div>
            </form>
          )}

          {step === "details" && (
            <form class="space-y-4" onSubmit={handleSignupComplete}>
              <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs">
                <span class="font-medium">{signupEmail.value}</span>
                <button
                  type="button"
                  onClick={() => {
                    // Zera dado pessoal antes de voltar — evita vazamento
                    // entre tentativas se outra pessoa usar o mesmo browser.
                    resetDownstreamFields();
                    signupStep.value = "email";
                  }}
                  class="ml-2 text-emerald-600 underline hover:no-underline"
                >
                  trocar
                </button>
              </div>

              <div class="space-y-2">
                <Label
                  htmlFor="signup-name"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  Nome completo *
                </Label>
                <Input
                  id="signup-name"
                  type="text"
                  required
                  autoComplete="name"
                  value={signupName.value}
                  onInput={(e: any) => (signupName.value = e.target.value)}
                  className="h-11"
                />
                <p class="text-[11px] text-muted-foreground/80">
                  Como aparece em documentos oficiais. Não fica visível pra
                  outros se você preencher o nome social abaixo.
                </p>
              </div>

              <div class="space-y-2">
                <Label
                  htmlFor="signup-social-name"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  Nome social (opcional)
                </Label>
                <Input
                  id="signup-social-name"
                  type="text"
                  autoComplete="off"
                  placeholder="O nome pelo qual você quer ser chamado"
                  value={signupSocialName.value}
                  onInput={(e: any) => (signupSocialName.value = e.target.value)}
                  className="h-11"
                />
                <p class="text-[11px] text-muted-foreground/80">
                  Se preenchido, é o nome que aparece pra outros usuários em
                  todos os lugares — chat, recibos, perfis. Você pode editar
                  depois em Minha Conta.
                </p>
              </div>

              <div class="space-y-2">
                <Label
                  htmlFor="signup-apelido"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  Apelido (opcional)
                </Label>
                <Input
                  id="signup-apelido"
                  type="text"
                  autoComplete="nickname"
                  placeholder="Como prefere ser chamado"
                  value={signupApelido.value}
                  onInput={(e: any) => (signupApelido.value = e.target.value)}
                  className="h-11"
                />
              </div>

              <div class="space-y-2">
                <Label
                  htmlFor="signup-phone"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  Telefone (opcional)
                </Label>
                <Input
                  id="signup-phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="(11) 99999-9999"
                  value={signupPhone.value}
                  onInput={(e: any) => (signupPhone.value = e.target.value)}
                  className="h-11"
                />
              </div>

              <div class="space-y-2">
                <Label
                  htmlFor="signup-password"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  Senha *
                </Label>
                <div class="relative">
                  <Input
                    id="signup-password"
                    type={signupShowPassword.value ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    placeholder="Mínimo 8 caracteres, letras + números"
                    value={signupPassword.value}
                    onInput={(e: any) => (signupPassword.value = e.target.value)}
                    className="h-11 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      (signupShowPassword.value = !signupShowPassword.value)
                    }
                    aria-label={
                      signupShowPassword.value ? "Ocultar senha" : "Mostrar senha"
                    }
                    title={
                      signupShowPassword.value ? "Ocultar senha" : "Mostrar senha"
                    }
                    class="absolute inset-y-0 right-0 grid w-11 place-items-center text-muted-foreground transition-colors hover:text-primary"
                  >
                    {signupShowPassword.value ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
              </div>

              <div class="space-y-2">
                <Label
                  htmlFor="signup-password-confirm"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  Confirmar senha *
                </Label>
                <Input
                  id="signup-password-confirm"
                  type={signupShowPassword.value ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={signupPasswordConfirm.value}
                  onInput={(e: any) =>
                    (signupPasswordConfirm.value = e.target.value)
                  }
                  className="h-11"
                />
              </div>

              <div class="space-y-2 border-t border-border pt-4">
                <Label
                  htmlFor="signup-referral"
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                >
                  Código de indicação (opcional)
                </Label>
                <Input
                  id="signup-referral"
                  type="text"
                  autoComplete="off"
                  placeholder="Ex: A7K3M2"
                  maxLength={6}
                  value={signupReferralCode.value}
                  onInput={(e: any) => {
                    const next = String(e.target.value ?? "")
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 6);
                    signupReferralCode.value = next;
                    signupReferralIndicatorName.value = null;
                    signupReferralInvalid.value = false;
                  }}
                  onBlur={() => void validateReferralCodeInline()}
                  className="h-11 uppercase tracking-widest"
                />
                {signupReferralChecking.value && (
                  <p class="text-[11px] text-muted-foreground">Validando código…</p>
                )}
                {signupReferralIndicatorName.value && (
                  <p class="text-[11px] text-emerald-700 dark:text-emerald-300">
                    ✓ Indicação de <strong>{signupReferralIndicatorName.value}</strong>
                  </p>
                )}
                {signupReferralInvalid.value && (
                  <p class="text-[11px] text-amber-700 dark:text-amber-300">
                    Código não encontrado — você pode seguir sem ele.
                  </p>
                )}
              </div>

              <p class="text-[11px] text-muted-foreground/80">
                Ao concluir, você entra no <strong>Plano Free</strong>. Pra
                acessar mais recursos, você pode contratar um pacote depois.
              </p>

              {signupErrorMsg.value && (
                <Alert
                  variant="destructive"
                  className="border-destructive/40 bg-destructive/10"
                >
                  <AlertCircle className="size-4" />
                  <AlertTitle>Não foi possível concluir</AlertTitle>
                  <AlertDescription>{signupErrorMsg.value}</AlertDescription>
                </Alert>
              )}

              <ShineButton
                type="submit"
                disabled={signupSubmitting.value}
                className="mt-2 h-11 w-full text-base font-medium"
              >
                {signupSubmitting.value ? "Criando conta…" : "Criar conta"}
              </ShineButton>

              <div class="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={backToLogin}
                  class="text-xs text-muted-foreground transition-colors hover:text-primary"
                >
                  ← Voltar ao login
                </button>
                <ThemeToggleButton />
              </div>
            </form>
          )}

          {step === "interest" && (
            <form class="space-y-4" onSubmit={handleSignupInterest}>
              <div class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
                <strong>{signupEmail.value}</strong> ainda não foi convidado.
                Conta um pouco sobre você — nosso time avalia e te envia o
                convite por email quando aprovar.
              </div>

              <div class="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Nome completo *
                </Label>
                <Input
                  required
                  autoComplete="name"
                  placeholder="Como devemos te chamar"
                  value={interestFullName.value}
                  onInput={(e: any) => (interestFullName.value = e.target.value)}
                  className="h-11"
                />
              </div>

              <div class="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Onde você estuda
                </Label>
                <Input
                  placeholder="Escola, faculdade, cursinho…"
                  value={interestStudyLocation.value}
                  onInput={(e: any) =>
                    (interestStudyLocation.value = e.target.value)
                  }
                  className="h-11"
                />
              </div>

              <div class="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  O quê (curso/área)
                </Label>
                <Input
                  placeholder="Medicina, ENEM, Direito, Concurso TJ…"
                  value={interestStudyArea.value}
                  onInput={(e: any) =>
                    (interestStudyArea.value = e.target.value)
                  }
                  className="h-11"
                />
              </div>

              <div class="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Por que tem interesse na plataforma?
                </Label>
                <textarea
                  rows={4}
                  maxLength={1000}
                  placeholder="Conta um pouco do seu contexto, objetivos, como soube da gente…"
                  value={interestMessage.value}
                  onInput={(e: any) =>
                    (interestMessage.value = e.target.value)
                  }
                  class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                />
              </div>

              {signupErrorMsg.value && (
                <Alert
                  variant="destructive"
                  className="border-destructive/40 bg-destructive/10"
                >
                  <AlertCircle className="size-4" />
                  <AlertTitle>Erro</AlertTitle>
                  <AlertDescription>{signupErrorMsg.value}</AlertDescription>
                </Alert>
              )}

              <ShineButton
                type="submit"
                disabled={signupSubmitting.value}
                className="mt-2 h-11 w-full text-base font-medium"
              >
                {signupSubmitting.value ? "Enviando…" : "Enviar pedido"}
              </ShineButton>

              <div class="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => {
                    // Zera dado pessoal antes de voltar — evita vazamento
                    // entre tentativas se outra pessoa usar o mesmo browser.
                    resetDownstreamFields();
                    signupStep.value = "email";
                  }}
                  class="text-xs text-muted-foreground transition-colors hover:text-primary"
                >
                  ← Trocar email
                </button>
                <button
                  type="button"
                  onClick={backToLogin}
                  class="text-xs text-muted-foreground transition-colors hover:text-primary"
                >
                  Voltar ao login
                </button>
              </div>
            </form>
          )}

          {step === "interest-done" && (
            <div class="space-y-4 text-center">
              <div class="mx-auto grid size-14 place-items-center rounded-full bg-emerald-500/10 text-emerald-600">
                <CheckCircle2 className="size-7" />
              </div>
              <h2 class="text-lg font-bold">Obrigado pelo interesse!</h2>
              <p class="text-sm text-muted-foreground">
                Você entrou na nossa lista de espera. Estamos sempre avaliando
                quem chega pra trazer novos alunos pra estudar com a gente —
                quando for a sua vez, você recebe um convite por email pra
                criar a conta. Fique de olho na caixa de entrada (e no spam).
              </p>
              <button
                type="button"
                onClick={backToLogin}
                className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                Voltar ao login
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </GlowFrame>
  );
}

function LoginPage() {
  return (
    <GlowFrame className="max-w-md">
      <Card className={CARD_CLASS}>
        <CardHeader className="items-center px-8 pb-4 pt-8 text-center">
          <CardTitle className="text-2xl">Entrar</CardTitle>
          <CardDescription>
            Suas credenciais do simulador continuam valendo.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-8 pt-0">
          <form class="space-y-5" onSubmit={handleLogin}>
            <div class="space-y-2">
              <Label
                htmlFor="email"
                className="text-xs uppercase tracking-wider text-muted-foreground"
              >
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="voce@email.com"
                value={formEmail.value}
                onInput={(e: any) => (formEmail.value = e.target.value)}
                className="h-11"
              />
              <label class="flex cursor-pointer select-none items-center justify-end gap-2 pt-0.5 text-[11px] text-muted-foreground">
                Lembrar neste dispositivo
                <Switch
                  checked={remember.value}
                  onCheckedChange={(v: boolean) => (remember.value = v)}
                />
              </label>
            </div>
            <div class="space-y-2">
              <Label
                htmlFor="password"
                className="text-xs uppercase tracking-wider text-muted-foreground"
              >
                Senha
              </Label>
              <div class="relative">
                <Input
                  id="password"
                  type={showPassword.value ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={formPassword.value}
                  onInput={(e: any) => (formPassword.value = e.target.value)}
                  className="h-11 pr-11"
                />
                <button
                  type="button"
                  onClick={() => (showPassword.value = !showPassword.value)}
                  aria-label={showPassword.value ? "Ocultar senha" : "Mostrar senha"}
                  title={showPassword.value ? "Ocultar senha" : "Mostrar senha"}
                  class="absolute inset-y-0 right-0 grid w-11 place-items-center text-muted-foreground transition-colors hover:text-primary focus:outline-none focus-visible:text-primary"
                >
                  {showPassword.value ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
              <div class="flex items-center justify-end">
                <button
                  type="button"
                  onClick={goToForgot}
                  class="text-[11px] text-muted-foreground transition-colors hover:text-primary"
                >
                  Esqueci minha senha
                </button>
              </div>
            </div>
            {errorMsg.value && (
              <Alert
                variant="destructive"
                className="border-destructive/40 bg-destructive/10"
              >
                <AlertCircle className="size-4" />
                <AlertTitle>Falha ao entrar</AlertTitle>
                <AlertDescription>{errorMsg.value}</AlertDescription>
              </Alert>
            )}
            <ShineButton
              type="submit"
              disabled={submitting.value}
              className="mt-2 h-11 w-full text-base font-medium"
            >
              {submitting.value ? "Entrando…" : "Entrar"}
            </ShineButton>
            {browserSupportsWebAuthn() && (
              <>
                <div class="relative my-1">
                  <div class="absolute inset-0 flex items-center" aria-hidden="true">
                    <div class="w-full border-t border-border" />
                  </div>
                  <div class="relative flex justify-center">
                    <span class="bg-card px-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                      ou
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePasskeyLogin}
                  disabled={submitting.value}
                  class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-4 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                >
                  {submitting.value ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Fingerprint className="size-4" />
                  )}
                  Entrar com biometria
                </button>
              </>
            )}
            <div class="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={goToSignup}
                class="text-xs text-muted-foreground transition-colors hover:text-primary"
              >
                Não tem conta? <span class="font-medium">Cadastre-se</span>
              </button>
              <ThemeToggleButton />
            </div>
          </form>
        </CardContent>
      </Card>
    </GlowFrame>
  );
}

function ContextChoice({ ctx }: { ctx: AvailableContext }) {
  const meta = roleMeta[ctx.role] ?? {
    icon: UserRound,
    label: ctx.role,
    tagline: ""
  };
  const Icon = meta.icon;
  const place = ctx.kind === "platform" ? "Plataforma" : ctx.companyName ?? "—";
  return (
    <button
      type="button"
      class="group block w-full text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
      disabled={submitting.value}
      onClick={() => handlePick(ctx)}
    >
      <Card className="border-border bg-card transition-all duration-200 group-hover:-translate-y-0.5 group-hover:border-primary/50 group-hover:shadow-md">
        <CardContent className="flex items-center gap-4 p-5">
          <FilledBrandBadge icon={Icon} />
          <div class="min-w-0 flex-1">
            <div class="font-semibold leading-tight">{meta.label}</div>
            {place && (
              <div class="mt-0.5 text-sm text-muted-foreground truncate">
                {place}
              </div>
            )}
            {meta.tagline && (
              <div class="mt-1 text-xs text-muted-foreground/70">
                {meta.tagline}
              </div>
            )}
          </div>
          <ChevronRight className="size-5 shrink-0 text-muted-foreground/60 transition-all group-hover:text-primary group-hover:translate-x-0.5" />
        </CardContent>
      </Card>
    </button>
  );
}

function ProfileChoice({ p }: { p: UserProfile }) {
  const meta = roleMeta[p.role] ?? {
    icon: UserRound,
    label: p.role,
    tagline: ""
  };
  const Icon = meta.icon;
  // Resumo do escopo: "Plataforma" ou "Nome da org" (única). Para role
  // que cobre múltiplas orgs (ex: student em várias), NÃO mostra contagem
  // — pedido 2026-05-19: o aluno não troca contexto, ele vê tudo junto,
  // mostrar "N organizações" só polui.
  const place =
    p.kind === "platform"
      ? "Plataforma"
      : p.companies.length === 1
      ? p.companies[0].name
      : "";
  return (
    <button
      type="button"
      class="group block h-full w-full text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
      disabled={submitting.value}
      onClick={() => handlePickProfile(p)}
    >
      <Card className="h-full border-border bg-card transition-all duration-200 group-hover:-translate-y-0.5 group-hover:border-primary/50 group-hover:shadow-md">
        <CardContent className="flex h-full items-center gap-4 p-5">
          <FilledBrandBadge icon={Icon} />
          <div class="min-w-0 flex-1">
            <div class="font-semibold leading-tight">{meta.label}</div>
            {place && (
              <div class="mt-0.5 text-sm text-muted-foreground truncate">
                {place}
              </div>
            )}
            {meta.tagline && (
              <div class="mt-1 text-xs text-muted-foreground/70">
                {meta.tagline}
              </div>
            )}
          </div>
          <ChevronRight className="size-5 shrink-0 text-muted-foreground/60 transition-all group-hover:text-primary group-hover:translate-x-0.5" />
        </CardContent>
      </Card>
    </button>
  );
}

function ContextPickerPage() {
  const u = user.value;
  return (
    <GlowFrame className="max-w-3xl">
      <Card className={CARD_CLASS}>
        <CardHeader className="px-6 pb-4 pt-6 sm:px-8 sm:pt-8">
          {/* Mobile: empilha (título → email → botões row). Desktop: lado a lado. */}
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div class="min-w-0">
              <CardTitle className="text-2xl">Escolha seu perfil</CardTitle>
              <CardDescription className="mt-1 break-words">
                {u ? (
                  <>
                    <span class="text-foreground/90 font-medium">{u.name}</span>
                    <span class="mx-2 opacity-50">·</span>
                    <span class="break-all">{u.email}</span>
                  </>
                ) : null}
              </CardDescription>
              <p class="mt-2 text-xs text-muted-foreground">
                {profiles.value.length === 0
                  ? "Nenhum perfil ativo."
                  : profiles.value.length === 1
                    ? "Você tem apenas 1 perfil — entrando…"
                    : `${profiles.value.length} perfis disponíveis`}
              </p>
            </div>
            <div class="flex shrink-0 items-center justify-end gap-2">
              <ThemeToggleButton />
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
              >
                <LogOut className="size-4 mr-1.5" />
                Sair
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-6 pb-6 pt-0 sm:px-8 sm:pb-8">
          {profiles.value.length === 0 ? (
            <Alert className="border-primary/30 bg-primary/5">
              <AlertCircle className="size-4 text-primary" />
              <AlertTitle>Sem perfil ativo</AlertTitle>
              <AlertDescription>
                Você está registrado na plataforma, mas ainda não foi adicionado
                a nenhuma Organização nem tem role administrativo.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div class="grid auto-rows-fr grid-cols-1 gap-3 sm:grid-cols-2">
                {sortedProfiles(profiles.value).map((p, i) => (
                  <ProfileChoice key={`${p.kind}-${p.role}-${i}`} p={p} />
                ))}
              </div>
              {/* Quando o user só tem 1 perfil mas chegou aqui via ?picker=1,
                  mostra um aviso elegante explicando. */}
              {profiles.value.length === 1 && wantsPicker() && (
                <Alert className="mt-5 border-primary/30 bg-primary/5">
                  <AlertCircle className="size-4 text-primary" />
                  <AlertDescription>
                    Você tem apenas este perfil cadastrado — não há outro pra
                    alternar. Acesse uma organização ou peça pra ser adicionado
                    a outra.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {errorMsg.value && (
            <Alert
              variant="destructive"
              className="mt-5 border-destructive/40 bg-destructive/10"
            >
              <AlertCircle className="size-4" />
              <AlertDescription>{errorMsg.value}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </GlowFrame>
  );
}

function RedirectPage() {
  const ctx = activeContext.value;
  const dest = destinationFor.value;
  const meta = ctx ? roleMeta[ctx.role] : null;
  const Icon = meta?.icon ?? UserRound;
  return (
    <GlowFrame className="max-w-md">
      <Card className={CARD_CLASS}>
        <CardHeader className="items-center text-center">
          <div class="mx-auto mb-2">
            <FilledBrandBadge icon={Icon} size="lg" />
          </div>
          <CardTitle className="text-2xl">Sessão pronta</CardTitle>
          <CardDescription>
            {ctx ? (
              <>
                Contexto ativo:{" "}
                <span class="font-medium text-foreground">
                  {meta?.label ?? ctx.role}
                </span>
                {ctx.companyName && (
                  <>
                    <span class="mx-1 opacity-50">·</span>
                    <span>{ctx.companyName}</span>
                  </>
                )}
              </>
            ) : (
              "Sem contexto"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {dest ? (
            <>
              <p class="text-center text-xs text-muted-foreground">
                Em produção redirecionaria automaticamente. Em dev cada painel
                roda em sua porta.
              </p>
              <a href={dest}>
                <ShineButton className="h-11 w-full text-base font-medium">
                  Ir para <code class="mx-1 font-mono text-sm">{dest}</code>
                  <ChevronRight className="size-4 ml-1" />
                </ShineButton>
              </a>
            </>
          ) : (
            <p class="text-center text-sm text-muted-foreground">
              Nenhum painel definido pra esse contexto ainda.
            </p>
          )}
          <Button
            variant="outline"
            className="h-10 w-full"
            onClick={() => (phase.value = "picker")}
          >
            Trocar de perfil
          </Button>
          <div class="flex items-center gap-2">
            <Button
              variant="outline"
              className="h-10 flex-1"
              onClick={handleLogout}
            >
              <LogOut className="size-4 mr-1.5" />
              Sair
            </Button>
            <ThemeToggleButton />
          </div>
        </CardContent>
      </Card>
    </GlowFrame>
  );
}

function LoadingPage() {
  return (
    <GlowFrame className="max-w-md">
      <Card className={CARD_CLASS}>
        <CardHeader>
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-4 w-48 mt-2" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </CardContent>
      </Card>
    </GlowFrame>
  );
}

// =============================================================================
// Root
// =============================================================================

export function App() {
  useEffect(() => {
    bootstrap();
    // Carrega branding pra trocar título + favicon + subtitle + footer
    // sem precisar reload. O signal reativo propaga em todos os <BrandHeader/>.
    void loadBranding();
  }, []);

  return (
    <>
      <MeshBackground />
      <main class="min-h-screen bg-background text-foreground">
        <div class="container mx-auto flex min-h-screen flex-col items-center justify-center px-4 py-12">
          <BrandHeader />
          {phase.value === "loading" && <LoadingPage />}
          {phase.value === "login" && <LoginPage />}
          {phase.value === "signup" && <SignupPage />}
          {phase.value === "forgot" && <ForgotPasswordPage />}
          {phase.value === "change-password" && <ChangePasswordPage />}
          {phase.value === "picker" && <ContextPickerPage />}
          {phase.value === "redirect" && <RedirectPage />}
          <BrandFooter />
        </div>
      </main>
    </>
  );
}
